import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Ban, Copy, KeyRound, Users, Trash2, MonitorSmartphone } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const useRegisters = () => useQuery({ queryKey: ["pos-registers"], queryFn: async () => (await api.get("/pos/admin/registers")).data });
const useMembers = () => useQuery({ queryKey: ["pos-members"], queryFn: async () => (await api.get("/pos/admin/members")).data });
const useStations = () => useQuery({ queryKey: ["pos-stations"], queryFn: async () => (await api.get("/pos/admin/stations")).data });

function RegisterDialog({ open, onClose, register, registers }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const editing = !!register;
  const [form, setForm] = useState(() => register
    ? { code: register.code, name: register.name, register_type: register.register_type, parent_id: register.parent_id || "", location: register.location || "" }
    : { code: "", name: "", register_type: "main", parent_id: "", location: "" });
  const [saving, setSaving] = useState(false);
  const mainOptions = (registers || []).filter((r) => r.register_type === "main" && r.is_active && r.id !== register?.id);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim(), name: form.name.trim(), register_type: form.register_type,
        parent_id: form.register_type === "sub" ? (form.parent_id || null) : null,
        location: form.location.trim() || null,
      };
      if (editing) await api.patch(`/pos/admin/registers/${register.id}`, payload);
      else await api.post("/pos/admin/registers", payload);
      toast.success(t("posadmin.created"));
      qc.invalidateQueries({ queryKey: ["pos-registers"] });
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="register-dialog">
        <DialogHeader><DialogTitle>{editing ? t("posadmin.edit_register") : t("posadmin.new_register")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>{t("posadmin.code")}</Label><Input value={form.code} onChange={(e) => set("code", e.target.value)} data-testid="register-code-input" /></div>
            <div className="space-y-1.5"><Label>{t("posadmin.name")}</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name-input" /></div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("posadmin.type")}</Label>
              <Select value={form.register_type} onValueChange={(v) => set("register_type", v)}>
                <SelectTrigger data-testid="register-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="main">{t("posadmin.type_main")}</SelectItem>
                  <SelectItem value="sub">{t("posadmin.type_sub")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.register_type === "sub" && (
              <div className="space-y-1.5">
                <Label>{t("posadmin.parent")}</Label>
                <Select value={form.parent_id} onValueChange={(v) => set("parent_id", v)}>
                  <SelectTrigger data-testid="register-parent-select"><SelectValue placeholder={t("posadmin.select_member")} /></SelectTrigger>
                  <SelectContent>
                    {mainOptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.code} — {r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="space-y-1.5"><Label>{t("posadmin.location")}</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="register-location-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="register-cancel-button">{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="register-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignmentsDialog({ open, onClose, register, members }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: assignments } = useQuery({
    queryKey: ["pos-assignments", register?.id],
    queryFn: async () => (await api.get(`/pos/admin/registers/${register.id}/assignments`)).data,
    enabled: open && !!register,
  });
  const assignedIds = new Set((assignments || []).map((a) => a.membership_id));
  const available = (members || []).filter((m) => !assignedIds.has(m.membership_id));

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["pos-assignments", register.id] }); };
  const add = async () => {
    if (!pick) return;
    setBusy(true);
    try { await api.post(`/pos/admin/registers/${register.id}/assignments`, { membership_id: pick }); setPick(""); invalidate(); }
    catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(false); }
  };
  const remove = async (mid) => {
    try { await api.delete(`/pos/admin/registers/${register.id}/assignments/${mid}`); invalidate(); }
    catch (e) { toast.error(apiErrorMessage(e, t)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="assignments-dialog">
        <DialogHeader><DialogTitle>{t("posadmin.assignments_for", { name: register?.name || "" })}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger className="sm:flex-1" data-testid="assign-member-select"><SelectValue placeholder={t("posadmin.select_member")} /></SelectTrigger>
              <SelectContent>
                {available.length === 0
                  ? <div className="px-2 py-1.5 text-sm text-slate-500">{t("posadmin.no_available_members")}</div>
                  : available.map((m) => <SelectItem key={m.membership_id} value={m.membership_id}>{m.name || m.email}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={add} disabled={busy || !pick} data-testid="assign-member-button"><Plus className="h-4 w-4" />{t("posadmin.assign_member")}</Button>
          </div>
          <div className="rounded-md border dark:border-slate-700">
            {(assignments || []).length === 0 ? (
              <p className="p-4 text-sm text-slate-500">{t("posadmin.no_assignments")}</p>
            ) : (
              <ul className="divide-y dark:divide-slate-700">
                {assignments.map((a) => (
                  <li key={a.membership_id} className="flex items-center justify-between p-3" data-testid={`assignment-row-${a.membership_id}`}>
                    <span className="text-sm">{a.name || a.email}</span>
                    <Button variant="ghost" size="sm" onClick={() => remove(a.membership_id)} data-testid={`unassign-${a.membership_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SetPinDialog({ open, onClose, member }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [emp, setEmp] = useState(member?.employee_code || "");
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/pos/admin/members/${member.membership_id}/pin`, { employee_code: emp.trim(), pin: pin.trim(), enabled: true });
      toast.success(t("posadmin.created"));
      qc.invalidateQueries({ queryKey: ["pos-members"] });
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="set-pin-dialog">
        <DialogHeader>
          <DialogTitle>{t("posadmin.set_pin_title", { name: member?.name || member?.email || "" })}</DialogTitle>
          <DialogDescription>{t("posadmin.pin_never_shown")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>{t("posadmin.employee_code")}</Label><Input value={emp} onChange={(e) => setEmp(e.target.value)} data-testid="pin-employee-input" /></div>
          <div className="space-y-1.5">
            <Label>{t("posadmin.pin")}</Label>
            <Input type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} maxLength={8} data-testid="pin-input" />
            <p className="text-xs text-slate-500">{t("posadmin.pin_hint")}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pin-cancel-button">{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="pin-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PairingCodeDialog({ open, onClose, station }) {
  const { t, fmtDate } = useI18n();
  const copy = () => { navigator.clipboard.writeText(station.pairing_code); toast.success(t("posadmin.code_copied")); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pairing-code-dialog">
        <DialogHeader>
          <DialogTitle>{t("posadmin.pairing_code_title")}</DialogTitle>
          <DialogDescription>{station?.label}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700/50 dark:bg-amber-950/40 dark:text-amber-300">
            {t("posadmin.pairing_code_warning")}
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded-md bg-slate-100 px-3 py-2 font-mono text-sm dark:bg-slate-800" data-testid="pairing-code-value">{station?.pairing_code}</code>
            <Button variant="outline" size="icon" onClick={copy} data-testid="pairing-code-copy"><Copy className="h-4 w-4" /></Button>
          </div>
          {station?.expires_at && <p className="text-xs text-slate-500">{t("posadmin.expires_at")}: {fmtDate(station.expires_at)}</p>}
        </div>
        <DialogFooter><Button onClick={onClose} data-testid="pairing-code-close">{t("common.close")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RegistersCard() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data: registers, isLoading } = useRegisters();
  const { data: members } = useMembers();
  const [dialog, setDialog] = useState(null); // {register}
  const [assign, setAssign] = useState(null);
  const [deactivate, setDeactivate] = useState(null);
  const regName = (id) => registers?.find((r) => r.id === id)?.code;

  const doDeactivate = async () => {
    try { await api.post(`/pos/admin/registers/${deactivate.id}/deactivate`); qc.invalidateQueries({ queryKey: ["pos-registers"] }); }
    catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setDeactivate(null); }
  };

  return (
    <Card>
      <div className="flex items-center justify-between p-4">
        <div><h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("posadmin.registers")}</h3><p className="text-xs text-slate-500">{t("posadmin.registers_desc")}</p></div>
        <Button size="sm" onClick={() => setDialog({ register: null })} data-testid="new-register-button"><Plus className="h-4 w-4" />{t("posadmin.new_register")}</Button>
      </div>
      {isLoading ? <div className="p-4"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        : (registers || []).length === 0 ? <p className="p-4 text-sm text-slate-500">{t("posadmin.no_registers")}</p>
          : (
            <div className="overflow-x-auto">
              <Table data-testid="registers-table">
                <TableHeader><TableRow>
                  <TableHead>{t("posadmin.code")}</TableHead><TableHead>{t("posadmin.name")}</TableHead>
                  <TableHead>{t("posadmin.type")}</TableHead><TableHead>{t("posadmin.status")}</TableHead>
                  <TableHead className="text-right">{t("common.actions")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {registers.map((r) => (
                    <TableRow key={r.id} data-testid={`register-row-${r.id}`}>
                      <TableCell className="font-medium">{r.code}</TableCell>
                      <TableCell>{r.name}{r.register_type === "sub" && r.parent_id && <span className="ml-1 text-xs text-slate-400">↳ {regName(r.parent_id)}</span>}</TableCell>
                      <TableCell>{t(r.register_type === "sub" ? "posadmin.type_sub" : "posadmin.type_main")}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant={r.is_active ? "default" : "secondary"}>{t(r.is_active ? "posadmin.active" : "posadmin.inactive")}</Badge>
                          {r.has_open_session && <Badge variant="outline">{t("posadmin.open_session_badge")}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setAssign(r)} data-testid={`assign-register-${r.id}`}><Users className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setDialog({ register: r })} data-testid={`edit-register-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                          {r.is_active && <Button variant="ghost" size="sm" onClick={() => setDeactivate(r)} data-testid={`deactivate-register-${r.id}`}><Ban className="h-4 w-4 text-red-500" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
      {dialog && <RegisterDialog open onClose={() => setDialog(null)} register={dialog.register} registers={registers} />}
      {assign && <AssignmentsDialog open onClose={() => setAssign(null)} register={assign} members={members} />}
      <AlertDialog open={!!deactivate} onOpenChange={(o) => !o && setDeactivate(null)}>
        <AlertDialogContent data-testid="deactivate-register-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("posadmin.confirm_deactivate_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("posadmin.confirm_deactivate_body", { name: deactivate?.name || "" })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDeactivate} data-testid="deactivate-confirm">{t("posadmin.deactivate")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function PinsCard() {
  const { t, fmtDate } = useI18n();
  const qc = useQueryClient();
  const { data: members, isLoading } = useMembers();
  const [setPin, setSetPin] = useState(null);

  const disable = async (mid) => {
    try { await api.post(`/pos/admin/members/${mid}/pin/disable`); qc.invalidateQueries({ queryKey: ["pos-members"] }); }
    catch (e) { toast.error(apiErrorMessage(e, t)); }
  };

  return (
    <Card>
      <div className="p-4"><h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("posadmin.pins")}</h3><p className="text-xs text-slate-500">{t("posadmin.pins_desc")}</p></div>
      {isLoading ? <div className="p-4"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        : (members || []).length === 0 ? <p className="p-4 text-sm text-slate-500">{t("posadmin.no_pos_members")}</p>
          : (
            <div className="overflow-x-auto">
              <Table data-testid="pins-table">
                <TableHeader><TableRow>
                  <TableHead>{t("posadmin.member")}</TableHead><TableHead>{t("posadmin.employee_code")}</TableHead>
                  <TableHead>{t("posadmin.pin_status")}</TableHead><TableHead>{t("posadmin.last_changed")}</TableHead>
                  <TableHead className="text-right">{t("common.actions")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {members.map((m) => (
                    <TableRow key={m.membership_id} data-testid={`pin-row-${m.membership_id}`}>
                      <TableCell className="font-medium">{m.name || m.email}</TableCell>
                      <TableCell className="text-slate-500">{m.employee_code || "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {!m.pin_set ? <Badge variant="secondary">{t("posadmin.pin_not_set")}</Badge>
                            : m.pin_enabled ? <Badge variant="default">{t("posadmin.pin_enabled")}</Badge>
                              : <Badge variant="secondary">{t("posadmin.pin_disabled")}</Badge>}
                          {m.locked && <Badge variant="destructive">{t("posadmin.locked")}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-500">{m.last_changed ? fmtDate(m.last_changed) : "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setSetPin(m)} data-testid={`set-pin-${m.membership_id}`}><KeyRound className="h-4 w-4" />{m.pin_set ? t("posadmin.reset_pin") : t("posadmin.set_pin")}</Button>
                          {m.pin_set && m.pin_enabled && <Button variant="ghost" size="sm" onClick={() => disable(m.membership_id)} data-testid={`disable-pin-${m.membership_id}`}><Ban className="h-4 w-4 text-red-500" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
      {setPin && <SetPinDialog open onClose={() => setSetPin(null)} member={setPin} />}
    </Card>
  );
}

function StationsCard() {
  const { t, fmtDate } = useI18n();
  const qc = useQueryClient();
  const { data: stations, isLoading } = useStations();
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [codeStation, setCodeStation] = useState(null);
  const [revoke, setRevoke] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pos/admin/stations", { label: label.trim() || "Station" });
      setCodeStation(data); setLabel("");
      qc.invalidateQueries({ queryKey: ["pos-stations"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(false); }
  };
  const doRevoke = async () => {
    try { await api.post(`/pos/admin/stations/${revoke.id}/revoke`); qc.invalidateQueries({ queryKey: ["pos-stations"] }); }
    catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setRevoke(null); }
  };
  const stateBadge = (s) => s.revoked
    ? <Badge variant="secondary">{t("posadmin.revoked_state")}</Badge>
    : s.paired ? <Badge variant="default">{t("posadmin.paired")}</Badge>
      : <Badge variant="outline">{t("posadmin.awaiting")}</Badge>;

  return (
    <Card>
      <div className="p-4"><h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("posadmin.stations")}</h3><p className="text-xs text-slate-500">{t("posadmin.stations_desc")}</p></div>
      <div className="flex flex-col gap-2 px-4 pb-4 sm:flex-row">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("posadmin.station_label")} data-testid="station-label-input" />
        <Button onClick={generate} disabled={busy} data-testid="generate-station-button"><MonitorSmartphone className="h-4 w-4" />{t("posadmin.generate_code")}</Button>
      </div>
      {isLoading ? <div className="p-4"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        : (stations || []).length === 0 ? <p className="px-4 pb-4 text-sm text-slate-500">{t("posadmin.no_stations")}</p>
          : (
            <div className="overflow-x-auto">
              <Table data-testid="stations-table">
                <TableHeader><TableRow>
                  <TableHead>{t("posadmin.station_label")}</TableHead><TableHead>{t("posadmin.status")}</TableHead>
                  <TableHead>{t("posadmin.expires_at")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {stations.map((s) => (
                    <TableRow key={s.id} data-testid={`station-row-${s.id}`}>
                      <TableCell className="font-medium">{s.label}</TableCell>
                      <TableCell>{stateBadge(s)}</TableCell>
                      <TableCell className="text-slate-500">{s.expires_at ? fmtDate(s.expires_at) : "—"}</TableCell>
                      <TableCell className="text-right">
                        {s.is_active && !s.revoked && <Button variant="ghost" size="sm" onClick={() => setRevoke(s)} data-testid={`revoke-station-${s.id}`}><Ban className="h-4 w-4 text-red-500" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
      {codeStation && <PairingCodeDialog open onClose={() => setCodeStation(null)} station={codeStation} />}
      <AlertDialog open={!!revoke} onOpenChange={(o) => !o && setRevoke(null)}>
        <AlertDialogContent data-testid="revoke-station-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("posadmin.confirm_revoke_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("posadmin.confirm_revoke_body", { label: revoke?.label || "" })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="revoke-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doRevoke} data-testid="revoke-confirm">{t("posadmin.revoke_station")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export function RegistersTab() {
  return (
    <div className="space-y-6" data-testid="registers-tab">
      <RegistersCard />
      <PinsCard />
      <StationsCard />
    </div>
  );
}
